/**
 * trade_map.ts — the World Atlas TRADE layer, pure (rows in, lines and
 * moving marks out). Three things move goods across the Commons and each
 * leaves a row the map can read:
 *
 * · THE ROADS — the regional route net (lib/regional_routes.ts): every
 *   settlement's tracks to its three nearest neighbours. The physical
 *   network; drawn thin and dim as the substrate every flow rides on.
 * · MARKET LINKS — `market_link`: a hamlet's or town's chosen market CITY
 *   (a trading habit, `trips` counts how often it has gone). Drawn as an
 *   arrowed line to the city, weight by log(trips).
 * · LIVE FLOWS — `caravan` rows (`status 'out'`, the ground lane between
 *   two settlements, goods + a purse physically in transit) and
 *   `cargo_convoy` rows (`status 'flying'`, the SKY lane a freighter flies
 *   with its escort). A flow's mark sits at the leg's time fraction, so
 *   every client draws the same caravan in the same place.
 */

export interface SiteXZ { x: number; z: number }
export interface FlowRow { fromSid: number; toSid: number; commodity: string; qty: number; departMicros: bigint; arriveMicros: bigint; status: string; kind: 'caravan' | 'convoy'; fromX?: number; fromZ?: number; toX?: number; toZ?: number }
export interface Flow { ax: number; az: number; bx: number; bz: number; t: number; commodity: string; qty: number; kind: 'caravan' | 'convoy'; fromSid: number; toSid: number }
export interface LinkRowT { settlementId: number; citySid: number; trips: number }
export interface LinkLine { ax: number; az: number; bx: number; bz: number; trips: number; weight: number; fromSid: number; citySid: number }

/** Time fraction along a leg at `now` (0 at departure, 1 at arrival; clamped). */
export function flowProgress(departMicros: bigint, arriveMicros: bigint, nowMicros: bigint): number {
  const span = Number(arriveMicros - departMicros);
  if (!(span > 0)) return 1;
  return Math.max(0, Math.min(1, Number(nowMicros - departMicros) / span));
}

/** The live flows with their endpoints resolved (a caravan by its settlements; a convoy carries its own). */
export function liveFlows(rows: FlowRow[], siteBySid: (sid: number) => SiteXZ | undefined, nowMicros: bigint): Flow[] {
  const out: Flow[] = [];
  for (const r of rows) {
    if (r.kind === 'caravan' ? r.status !== 'out' : r.status !== 'flying') continue;
    let ax: number, az: number, bx: number, bz: number;
    if (r.kind === 'convoy' && r.fromX !== undefined && r.fromZ !== undefined && r.toX !== undefined && r.toZ !== undefined) {
      ax = r.fromX; az = r.fromZ; bx = r.toX; bz = r.toZ;
    } else {
      const a = siteBySid(r.fromSid), b = siteBySid(r.toSid);
      if (!a || !b) continue;
      ax = a.x; az = a.z; bx = b.x; bz = b.z;
    }
    const t = flowProgress(r.departMicros, r.arriveMicros, nowMicros);
    if (t >= 1) continue; // landed — the sweep will settle it; nothing to draw
    out.push({ ax, az, bx, bz, t, commodity: r.commodity, qty: r.qty, kind: r.kind, fromSid: r.fromSid, toSid: r.toSid });
  }
  return out;
}

/** Market-link lines: each town's habit as a line to its city, weight 1..3 by log(trips). */
export function marketLinkLines(rows: LinkRowT[], siteBySid: (sid: number) => SiteXZ | undefined): LinkLine[] {
  const out: LinkLine[] = [];
  for (const r of rows) {
    if (!(r.citySid > 0) || r.citySid === r.settlementId) continue;
    const a = siteBySid(r.settlementId), b = siteBySid(r.citySid);
    if (!a || !b) continue;
    out.push({ ax: a.x, az: a.z, bx: b.x, bz: b.z, trips: r.trips, weight: 1 + Math.min(2, Math.log10(1 + Math.max(0, r.trips))), fromSid: r.settlementId, citySid: r.citySid });
  }
  return out;
}

/** The point a fraction `t` along a leg. */
export function alongLeg(f: { ax: number; az: number; bx: number; bz: number }, t: number): { x: number; z: number } {
  return { x: f.ax + (f.bx - f.ax) * t, z: f.az + (f.bz - f.az) * t };
}

/** Flows aggregated per settlement pair (for the legend / tooltip): how many caravans ride each lane now. */
export function laneLoad(flows: Flow[]): Map<string, { n: number; qty: number; kinds: Set<string> }> {
  const m = new Map<string, { n: number; qty: number; kinds: Set<string> }>();
  for (const f of flows) {
    const k = f.fromSid < f.toSid ? `${f.fromSid}-${f.toSid}` : `${f.toSid}-${f.fromSid}`;
    const e = m.get(k) ?? { n: 0, qty: 0, kinds: new Set<string>() };
    e.n++; e.qty += f.qty; e.kinds.add(f.commodity);
    m.set(k, e);
  }
  return m;
}
